import { getApiRoot } from './env';

export type PromotedTileVersions = Record<string, number>;

export interface PromotedTileVersionInfo {
  layers: PromotedTileVersions;
  cacheBust: number;
}

const TILE_CACHE_BUST_KEY = 'coordal_tile_cache_bust';
const TILE_LAYER_VERSIONS_KEY = 'coordal_tile_layer_versions';

/** Fallback when /tiles/versions is unreachable (offline dev). */
const ENV_CACHE_BUST = (import.meta as any).env?.VITE_TILES_CACHE_VERSION as string | undefined;

/**
 * Live promoted tile versions from the API.
 * Mapbox tile URLs append ?v={version} so browsers fetch fresh PBFs after promote
 * (CloudFront invalidation does not clear client disk cache for immutable tiles).
 */
export async function fetchPromotedTileVersions(signal?: AbortSignal): Promise<PromotedTileVersionInfo> {
  const apiRoot = getApiRoot();
  if (!apiRoot) return { layers: {}, cacheBust: 0 };

  try {
    const res = await fetch(`${apiRoot}/tiles/versions`, {
      signal,
      cache: 'no-store',
    });
    if (!res.ok) return { layers: {}, cacheBust: 0 };
    const data = await res.json();
    const layersRaw = data?.layers;
    const layers: PromotedTileVersions = {};
    if (layersRaw && typeof layersRaw === 'object') {
      for (const [layer, version] of Object.entries(layersRaw as Record<string, unknown>)) {
        const n = typeof version === 'number' ? version : Number(version);
        if (Number.isFinite(n) && n > 0) layers[layer] = n;
      }
    }
    const cacheBust = Number(data?.cache_bust) || 0;
    return { layers, cacheBust };
  } catch {
    return { layers: {}, cacheBust: 0 };
  }
}

function layerVersionsChanged(
  prev: PromotedTileVersions,
  next: PromotedTileVersions,
): boolean {
  for (const [layer, version] of Object.entries(next)) {
    if ((prev[layer] ?? 0) < version) return true;
  }
  return false;
}

/** Drop Mapbox GL tile cache when promote bumps cache_bust or any layer version (local survey patch). */
export async function clearMapboxTileCacheIfPromoted(
  cacheBust: number,
  layers: PromotedTileVersions = {},
): Promise<void> {
  if (typeof window === 'undefined') return;
  const prevCacheBust = Number(localStorage.getItem(TILE_CACHE_BUST_KEY) || 0);
  const prevLayersRaw = localStorage.getItem(TILE_LAYER_VERSIONS_KEY);
  const prevLayers: PromotedTileVersions = prevLayersRaw ? JSON.parse(prevLayersRaw) : {};
  const cacheBustChanged = cacheBust > prevCacheBust;
  const layersChanged = layerVersionsChanged(prevLayers, layers);
  if (!cacheBustChanged && !layersChanged) return;

  if (cacheBustChanged) {
    localStorage.setItem(TILE_CACHE_BUST_KEY, String(cacheBust));
  }
  if (layersChanged) {
    localStorage.setItem(TILE_LAYER_VERSIONS_KEY, JSON.stringify(layers));
  }

  const mapboxgl = await import('mapbox-gl');
  const clearStorage = (mapboxgl as { default?: { clearStorage?: (cb: () => void) => void } }).default
    ?.clearStorage;
  if (!clearStorage) return;

  await new Promise<void>((resolve) => {
    clearStorage(() => resolve());
  });
}

/** Append ?v= to a Mapbox tile template URL when we know the promoted layer version. */
export function appendTileCacheBust(url: string, layerVersions: PromotedTileVersions): string {
  const match = url.match(/\/tiles\/([^/]+)\/\{z\}/);
  if (!match) return url;
  const layerId = match[1];
  const version = layerVersions[layerId] ?? (ENV_CACHE_BUST ? Number(ENV_CACHE_BUST) : 0);
  if (!version) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${version}`;
}
